import React, { useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { Controller, FieldWrap } from '@leafwell/components';
import {
  Card,
  Heading,
  Paragraph,
  Wrap,
  Form,
  Button,
  TextFieldControlled as Input,
  SelectFieldMinimal,
} from '@leafwell/components';
import Line from './line';
import TextHighlight from './text-highlight';
import { useStatesList } from 'hooks/useStatesList';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useBrazeContext } from 'contexts/braze-context';
import { useEventsTracker } from 'contexts/eventsTracker';

const NewsletterForm: React.FC<{
  className?: string;
  filled?: boolean;
  id?: string;
}> = ({ className = '', filled = false, id = 'newsletter-signup' }) => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const {
    translations,
    newsletterForm: { label, title },
  } = useWordPressContext();
  const statesList = useStatesList();
  const posthog = usePostHog();
  const { trackEvent } = useEventsTracker();
  const { saveUser: saveBrazeUser } = useBrazeContext();

  async function handleSubmit({ name, email, state }) {
    // https://posthog.com/docs/product-analytics/identify#alias-assigning-multiple-distinct-ids-to-the-same-user
    posthog.alias(email);

    saveBrazeUser({
      name,
      email,
      customAttributes: {
        state: translations[state.toUpperCase()],
      },
    });

    trackEvent({
      event: 'form_submit',
      form_name: `formType_${window?.location?.pathname}`,
      form_destination: window?.location?.pathname,
      form_submit_text: 'Sign Up',
      formType: 'newsletter',
    });

    setFormSubmitted(true);
  }

  return (
    <Wrap id={id} size="large" className={className}>
      {!filled && <Line />}
      <Card
        variant="light"
        filled={filled}
        rounded={filled}
        className={[
          'lg:grid-cols-2-auto xl:grid-cols-2 md:items-center md:gap-x-14',
          filled ? 'md:py-16' : 'border-none py-reset',
        ].join(' ')}
      >
        <header>
          <Paragraph tagName="h3">
            <TextHighlight text={label} />
          </Paragraph>
          <Heading level="2" dangerouslySetInnerHTML={{ __html: title }} />
        </header>
        {formSubmitted ? (
          <div>
            <p className="text-center">
              Thanks for signing up for our newsletter.
            </p>
            <p className="text-center">
              We look forward to sharing our next update with you.
            </p>
          </div>
        ) : (
          <Form
            className="md:grid-flow-col md:grid-cols-3 md:gap-x-3 gap-y-3"
            mode="onSubmit"
            defaultValues={{
              name: '',
              email: '',
              state: '',
            }}
            shouldFocusError={false}
            onSubmit={handleSubmit}
          >
            <div>
              <Input
                className="rounded-lg"
                name="name"
                placeholder="Name"
                type="text"
              />
            </div>
            <div>
              <Input
                className="rounded-lg"
                name="email"
                placeholder="Email"
                type="email"
                validations={['required', 'email']}
              />
            </div>
            <div>
              <Controller
                name="state"
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <FieldWrap name="state" {...field} validations={['required']}>
                    <SelectFieldMinimal
                      {...field}
                      labelClassName="max-w-auto md:max-w-[140px] lg:max-w-[100px]"
                      aria-label="Select your state"
                      placeholder={translations['State']}
                      options={statesList}
                      error={!!error}
                      onSelect={({ value }) => field.onChange(value)}
                    />
                  </FieldWrap>
                )}
              />
            </div>
            <div>
              <Button
                type="submit"
                label="Sign Up"
                name="signup"
                className="w-full rounded-lg"
              />
            </div>
          </Form>
        )}
      </Card>
      {!filled && <Line />}
    </Wrap>
  );
};

export default NewsletterForm;
